import styled from 'styled-components';
import { baseVariables } from '../../styles';
import { MTimerAlignment } from './MTimer.types';

const { spacers } = baseVariables;

const inlineGap = spacers?.spacers?.[1] ?? '0.25rem';
const stackGap = spacers?.spacers?.[2] ?? '0.5rem';

export const Wrapper = styled.div<{ $alignment?: MTimerAlignment }>`
  display: ${({ $alignment }) => ($alignment ? 'flex' : 'inline-flex')};
  flex-direction: ${({ $alignment }) => ($alignment ? 'column' : 'row')};
  align-items: ${({ $alignment }) => ($alignment === MTimerAlignment.Left ? 'flex-start' : 'center')};
  gap: ${({ $alignment }) => ($alignment ? stackGap : inlineGap)};
  color: inherit;
`;

export const SkeletonWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${inlineGap};
  color: inherit;

  @keyframes react-loading-skeleton {
    100% {
      transform: translateX(100%);
    }
  }

  .react-loading-skeleton {
    --base-color: var(--t-neutral-color-10, #ebebeb);
    --highlight-color: var(--t-neutral-color-05, #f5f5f5);
    --animation-duration: 1.5s;
    --animation-direction: normal;
    --pseudo-element-display: block;

    background-color: var(--base-color);
    border-radius: ${inlineGap};
    display: inline-flex;
    position: relative;
    user-select: none;
    overflow: hidden;
  }

  .react-loading-skeleton::after {
    content: ' ';
    display: var(--pseudo-element-display);
    position: absolute;
    inset-block-start: 0;
    inset-inline: 0;
    block-size: 100%;
    background-repeat: no-repeat;
    background-image: linear-gradient(90deg, var(--base-color) 0%, var(--highlight-color) 50%, var(--base-color) 100%);
    transform: translateX(-100%);
    animation-name: react-loading-skeleton;
    animation-direction: var(--animation-direction);
    animation-duration: var(--animation-duration);
    animation-timing-function: ease-in-out;
    animation-iteration-count: infinite;
  }

  @media (prefers-reduced-motion) {
    .react-loading-skeleton {
      --pseudo-element-display: none;
    }
  }
`;

export const TimerDisplay = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${inlineGap};
  color: inherit;
`;

export const TimerUnit = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: ${inlineGap};
  color: inherit;

  p,
  span {
    text-transform: uppercase;
  }
`;

export const SkeletonDigit = styled.div`
  padding-block-end: ${inlineGap};
  display: inline-block;
  font-size: 1.875rem;
  line-height: 1;
  font-weight: 700;
  inline-size: auto;
  min-block-size: 1.875rem;
`;

export const SkeletonLabel = styled.div`
  padding-block-end: ${inlineGap};
  display: flex;
  align-items: center;
  justify-content: center;
  inline-size: 100%;
  line-height: 1.5;
  font-size: 0.75rem;
  min-block-size: 0.75rem;
`;

export const Separator = styled.span`
  font-size: 1.375rem;
  font-weight: 500;
  line-height: 1.25;
  color: inherit;
  padding-inline: 0.125rem;
  padding-block-end: ${inlineGap};
  font-family: var(--t-font-family);
`;

export const ScreenReaderOnly = styled.div`
  position: absolute;
  inline-size: 1px;
  block-size: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
`;
